import React, { Fragment, useState, useEffect, useMemo, } from 'react';
import { Input, Button, } from 'reactstrap';
import { useCounter, useToggle } from 'react-use';
import { sortBy, pick, difference, keyBy, get, orderBy, isEmpty, omit, sumBy, groupBy, } from 'lodash';
import numeral from 'numeral';
import { useBoolean, useList, } from 'react-use';
import fileDownload from 'js-file-download';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import qs from 'qs';
import { format as formatDate, addHours, } from 'date-fns';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import Select from 'react-select';

import env from '../../env';
import firebase, { functions } from '../../firebase';
import { idWithTenant, } from '../../shared/util';
import { boxSizes, amazonProductSizeTypes, } from '../../shared/config';
import { batch, getCollectionData, getAllCollectionDataByChunk, } from '../../shared/firebase';
import { fields, amazonProductFields, computeInventory, deliveryRoutes, conditions, } from '../../shared/models/product';
import { title, price, condition, deliveryRouteValue, cmSizes, computeAmazonProductSizeType, } from '../../shared/models/amazonProduct';
import { newId, createProductsWithSkuCheck } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useQueryParams from '../hooks/useQueryParams';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import usePagination from '../hooks/usePagination';
import Page from '../hocs/Page';
import ModelFormModal from '../modals/ModelFormModal';
import ProductInventoryHistoryModal from '../modals/ProductInventoryHistoryModal';
import AmazonProductAddModal from '../modals/AmazonProductAddModal';
import Field from '../Field';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ImportButton from '../ImportButton';
import ModalButton from '../ModalButton';
import ProgressButton from '../ProgressButton';
import QuerySortLabel from '../QuerySortLabel';
import QuerySelector from '../QuerySelector';
import QueryInput from '../QueryInput';
import QueryBoolean from '../QueryBoolean';
import Pagination from '../Pagination';
import DataToggle from '../DataToggle';
import ProductsTabs from '../ProductsTabs';
import ProductTagsDisplay from '../ProductTagsDisplay';
import OverlayLoading from '../OverlayLoading';

const countPerPage = 100;
const maxAmazonProductsCount = 10000;
const { keys, values, entries } = Object;
const db = firebase.firestore();
const fetchAmazonProducts = functions.httpsCallable('fetchAmazonProducts');
const updateDeliveryRoute = functions.httpsCallable('updateDeliveryRoute');
const updateAmazonProduct = functions.httpsCallable('updateAmazonProduct');
const sortIterateees = {
  id: _ => _.createdAt.toDate(),
};
const deliveryRoutesByLabel = keyBy(entries(deliveryRoutes).map(([k, v]) => ({ key: k, ...v })), 'label');
const conditionsByLabel = keyBy(entries(conditions).map(([k, v]) => ({ key: k, ...v })), 'label');
const postAmazonProducts = functions.httpsCallable('postAmazonProducts');

export default Page(function AmazonProducts (props) {
  const { user, tenant, history, } = props;
  const queryParams = useQueryParams();
  const isOverJuly = new Date() > new Date('2025/08/01 00:00:00');
  const {
    showsAll,
    onlyHasInventory,
    onlyNoFnsku,
    sort = { field: 'id', direction: 'desc', },
    keyword,
  } = queryParams;
  const isSearching = !isEmpty(keyword);
  const [componentVersion, { inc: updateComponentVersion }] = useCounter(0);
  const extraSellerIdForFilter = queryParams.extraSellerId?.[0] || null;
  const extraSellerId = extraSellerIdForFilter === tenant.id ? null : extraSellerIdForFilter;
  const [showsAmazonProductAddModal, toggleAmazonProductAddModal] = useBoolean(false);
  const productsRef = tenant.ref.collection('products');
  const extraSellers = useCollectionSubscription(tenant.ref.collection('extraSellers').orderBy('createdAt'), [tenant.id]);
  const extraSellersById = keyBy(extraSellers, 'id');

  const { items: productChunks, isLoading: isLoadingProductChunks } = useCollectionSubscription(tenant.ref.collection('productChunks'), [tenant.id], { detail: true, initialItems: null, });
  const chunkProducts = useMemo(() => orderBy(productChunks?.flatMap(_ => entries(omit(_, ['id', 'ref', 'exists'])).map(([k, v]) => ({ id: k, ref: tenant.ref.collection('products').doc(k), ...v, })).map(_ => ({ ..._, fullId: idWithTenant(_.ref) }))), _ => numeral(_.id).value(), 'desc'), [tenant, productChunks]);

  // NOTE: 絞り込み
  const filterChunkProducts = () => {
    let filteredChunkProducts = chunkProducts;
    filteredChunkProducts = filteredChunkProducts
      .filter(_ => _.isAmazonProduct && !_.isCase)
      .filter(_ => _.extraSellerId === extraSellerId);
    if(isSearching) {
      if(!isEmpty(keyword)) {
        filteredChunkProducts = filteredChunkProducts.filter(_ => [_.sku, _.name].some(_ => _.includes(queryParams.keyword)));
      }
    }
    return filteredChunkProducts;
  };

  const filteredChunkProducts = useMemo(() => filterChunkProducts(), [chunkProducts, extraSellerId, keyword]);
  const pagination = usePagination({ countPerPage, totalCount: filteredChunkProducts.length, });
  const { pageNumber, offset, } = pagination;
  const { items: products, isLoading } = useDocumentsFetch(productChunks != null && filteredChunkProducts.slice(offset, offset + countPerPage).map(_ => _.ref), [tenant, filteredChunkProducts, pageNumber, componentVersion], { detail: true, initialItems: null });
  const caseProducts = useCollectionSubscription(tenant.ref.collection('products').where('isCase', '==', true));
  const caseUsingProductIds = caseProducts.flatMap(_ => _.items || []).map(_ => _.productId);
  const caseInventoryByProductId = caseProducts.reduce((x, caseProduct) => {
    const caseGoodInventory = (caseProduct.goodInventoryIds || []).length;
    return (caseProduct.items || []).reduce((x, item) => ({
      ...x,
      [item.productId]: (x[item.productId] || 0) + caseGoodInventory * item.quantity,
    }), x);
  }, {});
  const receivingRequestsRef = tenant.ref.collection('receivingRequests');
  const shippingRequestsRef = tenant.ref.collection('shippingRequests');
  const receivingRequests = useCollectionSubscription(products != null && receivingRequestsRef.where('status', '!=', 'received'), [products, tenant.id]);
  const shippingRequests = useCollectionSubscription(products != null && shippingRequestsRef.where('status', 'not-in', ['shipped', 'discarded', 'cancelled']).orderBy('status'), [products, tenant.id]);
  const receivingRequestItemsGroupedByProductId = groupBy(receivingRequests.map(_ => _.items || []).flat(), 'productId');
  const shippingRequestItemsGroupedByProductId = groupBy(shippingRequests.map(_ => _.items || []).flat(), 'productId');
  const extraSellerOptions = [{ ...tenant, name: 'メイン' }, ...extraSellers].map(_ => ({ value: _.id, label: _.name, data: _, }));
  const productItems = (products || []).filter(_ => _.isAmazonProduct && !_.isCase).map((product) => {
    const { id } = product;
    const receivingRequestItems = receivingRequestItemsGroupedByProductId[id] || [];
    const receivingCount = sumBy(receivingRequestItems, 'quantity')
    const { goodInventory, ngInventory, waitingToShippingGoodCount, waitingToShippingNgCount, goodInventoryWithoutAllocation, ngInventoryWithoutAllocation } = computeInventory(product, shippingRequests);
    return {
      ...product,
      receivingRequestItems,
      receivingCount,
      goodInventory,
      ngInventory,
      waitingToShippingGoodCount,
      waitingToShippingNgCount,
      goodInventoryWithoutAllocation,
      ngInventoryWithoutAllocation,
    };
  });

  let filteredProductItems = productItems;
  if(showsAll !== '1') {
    filteredProductItems = filteredProductItems.filter(_ => !_.hides);
  }
  if(onlyHasInventory === '1') {
    filteredProductItems = filteredProductItems.filter(_ => _.goodInventory > 0);
  }
  if(onlyNoFnsku === '1') {
    filteredProductItems = filteredProductItems.filter(_ => _.fnsku == null);
  }

  const sortedProductItems = orderBy(filteredProductItems, (sortIterateees[sort.field] || sort.field), sort.direction);
  const hiddenCount = productItems.length - filteredProductItems.length;
  const [lastProduct] = orderBy(chunkProducts, _ => parseInt(_.id.replace(/\D/g, ''), 10), 'desc');
  const newRef = !isLoadingProductChunks && productsRef.doc(newId(lastProduct));
  const onClickDownloadSampleCsv = () => {
    const data = [
      {
        ASIN: 'B07HFQF5MG',
        SKU: 'sku_123456',
        品名: 'くつ下',
        販売価格: '3000',
        配送方法: 'Amazonから配送',
        コンディション説明: '',
      },
    ];
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const content = encoder.encode(unparseCsv(data));
    fileDownload(content, 'Amazon商品マスタ_サンプル.csv');
  };
  const validate = async (rows, row, i) => {
    const {
      ASIN: asin,
      SKU: sku,
      品名: itemName,
      販売価格: priceString,
      配送方法: deliveryRouteLabel,
      コンディション: conditionLabel = '新品',
      コンディション説明: conditionNote,
    } = row;
    const { data: [amazonProduct] } = await fetchAmazonProducts({ tenantId: tenant.id, idType: 'ASIN', ids: [asin], });
    const { data: [amazonProductBySku] } = await fetchAmazonProducts({ tenantId: tenant.id, idType: 'SKU', ids: [sku], });
    const price = parseInt(priceString, 10);
    const deliveryRoute = get(deliveryRoutesByLabel, [deliveryRouteLabel, 'key']);
    const condition = get(conditionsByLabel, [conditionLabel, 'key']);
    const otherSkus = [...chunkProducts.filter(_ => _.extraSellerId === extraSellerId).map(_ => _.sku), ...rows.slice(0, i).map(_ => _.SKU)];
    const errors = [
      sku.match(/\s/) && 'SKUに空白やタブ等が含まれています',
      !asin && 'ASINは必須です',
      !amazonProduct?.AttributeSets && 'ASINが見つかりません',
      !sku && 'SKUは必須です',
      amazonProductBySku?.AttributeSets && 'SKUがすでにAmazon側に存在します',
      otherSkus.includes(sku) && 'SKUが他に存在します',
      (itemName && itemName.length > 16) && '品名は16文字以内です',
      !(price > 0) && '販売価格は1円以上で指定してください',
      deliveryRoute == null && '配送方法の指定が正しくありません',
      condition == null && 'コンディションの指定が正しくありません',
    ];
    return {
      source: row,
      errors: errors.filter(_ => _),
      amazonProduct,
      asin,
      sku,
      itemName,
      price,
      deliveryRoute,
      condition,
      conditionNote,
    }
  };
  const processRows = async (rows) => {
    const allRows = rows;
    const validatedRows = await rows
      .filter(_ => _['ASIN'])
      .reduce(async (x, row, i) => {
        const prevs = await x;

        return [...prevs, (await Promise.all([validate(allRows, row, i), new Promise(_ => setTimeout(_, 400))]))[0]];
      }, Promise.resolve([]));
    if(validatedRows.filter(_ => !isEmpty(_.errors)).length > 0) {
      const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
      const content = encoder.encode(unparseCsv(validatedRows.map((row) => {
        return {
          ...row.source,
          エラー内容: row.errors.join('\n'),
        };
      })));
      fileDownload(content, 'エラー結果.csv');
      throw new Error();
    } else {

      await postAmazonProducts({
        extraSellerId,
        tenantId: tenant.id,
        products: validatedRows.map(_ => omit(_, ['source', 'errors'])),
      });
      const rowsForCreate = validatedRows.map((row, i) => {
        const { asin, amazonProduct, } = row;
        return {
          productId: (parseInt(newId(lastProduct), 10) + i).toString(),
          sku: row.sku,
          data: {
            ...omit(row, ['source', 'errors']),
            asin,
            name: title(amazonProduct),
            itemName: row.itemName || title(amazonProduct).slice(0, 10),
            amazonProduct,
            isAmazonProduct: true,
            fnsku: null,
            postedInventoryAvailabilityCount: 0,
            requestsNeko: false,
            goodInventory: 0,
            ngInventory: 0,
            shippingSize: null,
            extraSellerId,
            createdAt: new Date(),
          },
        };
      });
      await createProductsWithSkuCheck(db, productsRef, rowsForCreate);
    }
  };
  const renderFormModalFooterContent = (base, { onSubmit, isUnsubmittable, isSubmitting }) => {
    const onClickWithReceivingRequest = async (event) => {
      await onSubmit(event);
      history.push(`/receivingRequests?newProductId=${newRef.id}`);
    };
    const onClickWithReceivingRequestAndShippingRequest = async (event) => {
      await onSubmit(event);
      const [lastReceivingRequest] = await getCollectionData(receivingRequestsRef.orderBy('createdAt', 'desc').limit(1));
      await receivingRequestsRef.doc('W' + newId(lastReceivingRequest)).set({
        items: [{ productId: newRef.id, quantity: 1, goodQuantity: 0, ngQuantity: 0 }],
        checkNote: null,
        documentNumber: null,
        pendingItems: [],
        receivingOptionValues: [],
        setNote: null,
        status: 'initial',
        productIds: [newRef.id],
        createdAt: new Date(),
      });
      toast.success('入荷依頼を作成しました');
      history.push(`/shippingRequests?${qs.stringify({ newItems: [{ productId: newRef.id, goodQuantity: 1, }], newType: 'normal' })}`);
    };
    return (
      <Fragment>
        {base}
        <Button className="save" type="submit" color="primary" onClick={onClickWithReceivingRequest} disabled={isUnsubmittable || isSubmitting}>入荷依頼する</Button>
      </Fragment>
    );
  };
  useEffect(() => {
    user.ref.set({ preferences: { productsTab: '/amazonProducts', } }, { merge: true });
  }, []);

  return (
    <div className="products container-fluid">
      <div className="p-4 bg-white my-4">
        <ProductsTabs activeTab="amazonProducts" shippingRequests={shippingRequests} tenant={tenant} />
        <div className="d-flex justify-content-start align-items-end mb-1 gap-1">
          {tenant.tenantOptionValues?.includes('multiSellers') && <QuerySelector paramName="extraSellerId" options={extraSellerOptions} label="ショップ選択" defaultValue={[extraSellerOptions[0].value]} />}
        </div>
        {
          tenant.spApi == null ? (
            <div className="alert alert-danger">
              右上メニューからSP API認証を行なってください。
            </div>
          ) : (
            <div className="d-flex justify-content-between align-items-end mb-1 gap-1">
              <div className="d-flex gap-1 align-items-end">
                <QueryInput paramName="keyword" label="SKU, 商品名で絞り込み" />
                <div>
                  {hiddenCount > 0 && <div className="text-warning">このページでは{hiddenCount}件が非表示になっています</div>}
                  <div className="d-flex gap-2 align-items-end">
                    <QueryBoolean paramName="showsAll" label="非表示も表示" defaultValue={'0'} />
                    <QueryBoolean paramName="onlyHasInventory" label="在庫ありのみ" defaultValue={'0'} />
                    <QueryBoolean paramName="onlyNoFnsku" label="FNSKU未取得のみ" defaultValue={'0'} />
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-start align-items-end gap-1">
                <Pagination {...pagination} />
                <Button onClick={onClickDownloadSampleCsv}>
                  <span className="fas fa-download mr-1" />
                  サンプルCSVダウンロード
                </Button>
                <div>
                  {
                    isOverJuly && (
                      <div className="text-muted small">
                        メンテナンス中
                      </div>
                    )
                  }
                  <ImportButton processRows={processRows} disabled={isLoadingProductChunks || isOverJuly} />
                </div>
                <ModalButton
                  disabled={isLoadingProductChunks}
                  label="Amazonに出品中の商品を取り込む"
                  content={_ => <AmazonProductSelectModalContent {...{ tenant, products, chunkProducts, lastProduct, extraSellers, }} />}
                  modalProps={{ style: { minWidth: '80vw' } }}
                />
                <div>
                  {
                    isOverJuly && (
                      <div className="text-muted small">
                        メンテナンス中
                      </div>
                    )
                  }
                  <Button color="primary" onClick={toggleAmazonProductAddModal.bind(null, true)} disabled={isOverJuly}>
                    <span className="mr-1 fas fa-plus" />
                    Amazonから商品を検索して登録する
                  </Button>
                </div>
              </div>
            </div>
          )
        } 
        {sortedProductItems.length > 1000 && <div className="alert alert-warning">1,000件まで表示しています。</div>}
        <div className="overflow-auto position-relative" style={{ zIndex: 0 }}>
          {
            sortedProductItems.length > 0 ? (
              <table className="table table-sm table-bordered sticky-table">
                <thead className="thead-light text-center">
                  <tr>
                    <th style={{ minWidth: 150 }}>
                      SKU
                      <br />
                      登録日
                      <div className="small">商品ID</div>
                      {
                        tenant.tenantOptionValues?.includes('multiSellers') && (
                          <div>ショップ</div>
                        )
                      }
                    </th>
                    <th style={{ minWidth: 80 }}>
                      画像
                    </th>
                    <th style={{ minWidth: 150 }}>
                      商品名
                      <br />
                      品名
                      <br />
                      サイズ
                    </th>
                    <th style={{ minWidth: 180 }}>
                      販売価格
                      <br />
                      配送方法
                      <br />
                      コンディション
                    </th>
                    <th style={{ minWidth: 180 }}>
                      JANまたはバーコード情報
                      <br />
                      ASIN
                      <br />
                      FNSKU
                    </th>
                    <th style={{ minWidth: 60 }}>
                      在庫
                    </th>
                    <th style={{ minWidth: 90 }}>
                      販売不可在庫
                    </th>
                    <th style={{ minWidth: 180 }}>入出荷作業中</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    sortedProductItems.map((productItem) => {
                      const { id, ref, amazonProduct, isAmazonProduct, name, sku, barcodeInfo, itemName, requestsNeko = false, goodInventory, ngInventory, fnsku, asin, receivingCount, waitingToShippingGoodCount, waitingToShippingNgCount, goodInventoryWithoutAllocation, ngInventoryWithoutAllocation, createdAt, price, deliveryRoute, condition, shippingSize, extraSellerId, shopifyInventoryItemId, } = productItem;
                      const imageUrl = get(amazonProduct, 'AttributeSets.ItemAttributes.SmallImage.URL');
                      const isUsedByCase = caseUsingProductIds.includes(id);
                      const beforeUpdate = async (newValues) => {
                        if(deliveryRoute !== newValues.deliveryRoute) {
                          await updateDeliveryRoute({ sku, tenantId: tenant.id, productId: id, productType: amazonProduct.summaries[0].productType, deliveryRoute: newValues.deliveryRoute, quantity: goodInventory, extraSellerId, });
                        }
                      };
                      const beforeDelete = async () => {
                        const [receivingRequest] = await getCollectionData(tenant.ref.collection('receivingRequests').where('productIds', 'array-contains', id).limit(1));
                        const [shippingRequest] = await getCollectionData(tenant.ref.collection('shippingRequests').where('productIds', 'array-contains', id).limit(1));
                        if(receivingRequest || shippingRequest) {
                          toast.error('一度でも入出荷があった商品は削除できません。');
                          return false;
                        }
                      };
                      const onClickUpdateAmazonProduct = async () => {
                        if (!window.confirm('Amazon商品情報を最新に更新しますか？')) return;

                        try {
                          const result = await updateAmazonProduct({ productId: id });
                          toast.success(result.data.message);
                          updateComponentVersion();
                        } catch (error) {
                          console.error(error);
                          toast.error(error.message || '商品情報の更新に失敗しました');
                        }
                      };


                      return (
                        <tr key={id}>
                          <td data-id={id}>
                            <div>
                              <span className="font-weight-bold">SKU:</span> {sku}
                            </div>
                            <div>{formatDate(createdAt.toDate(), 'yyyy/MM/dd')}</div>
                            <div className="small">{idWithTenant(ref)}</div>
                            {tenant.tenantOptionValues?.includes('multiSellers') && <div>{extraSellerId ? extraSellersById[extraSellerId]?.name : 'メイン'}</div>}
                            {requestsNeko && <div className="badge badge-warning">ポスト便希望</div>}
                            <ProductTagsDisplay product={productItem} productTags={[]} />
                            {tenant.tenantOptionValues?.includes('shopify') && shopifyInventoryItemId != null && <div className="badge badge-info">Shopify</div>}
                            {user.dev && <a href={`https://console.firebase.google.com/u/0/project/${env('FIREBASE_PROJECT_ID')}/firestore/data/~2Ftenants~2F${tenant.id}~2Fproducts~2F${id}`} target="_blank">F</a>}
                          </td>
                          <td>
                            <img src={imageUrl} style={{ maxHeight: 100, maxWidth: 100, }} />
                          </td>
                          <td>
                            <div>
                              {name}
                            </div>
                            <div className="font-weight-bold">
                              {itemName}
                            </div>
                            <div className="font-weight-bold">
                              {
                                shippingSize ? (
                                  boxSizes[shippingSize]?.label
                                ) : (
                                  cmSizes(amazonProduct).map(_ => Math.round(_) + 'cm').join(' x ')
                                )
                              }
                              {
                                deliveryRoute === 'amazon' && (
                                  <span>({amazonProductSizeTypes[computeAmazonProductSizeType(amazonProduct)].label})</span>
                                )
                              }
                            </div>
                          </td>
                          <td>
                            <div>
                              {numeral(price).format()}
                            </div>
                            <div>
                              {get(deliveryRoutes, [deliveryRoute, 'label'])}
                            </div>
                            <div>
                              {get(conditions, [condition, 'label'])}
                            </div>
                          </td>
                          <td>
                            <div>
                              <span className="font-weight-bold">JAN:</span> {barcodeInfo}
                            </div>
                            {isAmazonProduct && <div className="font-weight-bold">ASIN: {asin}</div>}
                            {isAmazonProduct && <div className="font-weight-bold">FNSKU: {fnsku}</div>}
                          </td>
                          <td className="text-right">
                            <div>{numeral(goodInventory).format('0,0')}</div>
                            {caseInventoryByProductId[id] > 0 && <div className="small text-muted text-nowrap">ケース内 {numeral(caseInventoryByProductId[id]).format('0,0')}</div>}
                          </td>
                          <td className="text-right">
                            {numeral(ngInventory).format('0,0')}
                          </td>
                          <td>
                            <table className="mb-0 table table-sm table-borderless">
                              <tbody>
                                <tr>
                                  <th>入荷依頼中</th>
                                  <td className="text-right">{numeral(receivingCount).format('0,0')}</td>
                                </tr>
                                <tr>
                                  <th>出荷依頼中</th>
                                  <td className="text-right">{numeral(waitingToShippingGoodCount).format('0,0')}</td>
                                </tr>
                                <tr>
                                  <th>返送・廃棄依頼中</th>
                                  <td className="text-right">{numeral(waitingToShippingNgCount).format('0,0')}</td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1 text-nowrap">
                              <ProgressButton size="sm" color="info" process={onClickUpdateAmazonProduct}>
                                更新
                              </ProgressButton>
                              <EditButton size="sm" itemRef={ref} FormModal={ModelFormModal} beforeUpdate={beforeUpdate} formProps={{ title: '商品 編集', fields: { ...omit(fields({ tenant }), ['name', 'sku', 'price', 'priceTaxType']), ...pick(amazonProductFields(), ['deliveryRoute']), } }} onFinish={updateComponentVersion} />
                              <DeleteButton size="sm" item={productItem} itemRef={ref} disabled={isUsedByCase || [goodInventory, ngInventory, receivingCount, waitingToShippingGoodCount, waitingToShippingNgCount].some(_ => _ !== 0)} beforeDelete={beforeDelete} onFinish={updateComponentVersion} />
                              <DataToggle data={productItem} fieldName="hides" label="非表示" inputProps={{ style: { transform: 'initial' }, }} labelProps={{ className: 'small', }} onFinish={updateComponentVersion} />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                商品は未登録です
              </div>
            )
          }
        </div>
      </div>
      {showsAmazonProductAddModal && <AmazonProductAddModal extraSellerId={extraSellerId} tenant={tenant} isOpen onClickClose={toggleAmazonProductAddModal.bind(null, false)} newRef={newRef} renderFooterContent={renderFormModalFooterContent} otherProducts={chunkProducts} />}
    </div>
  );
});

function AmazonProductSelectModalContent (props) {
  const { tenant, chunkProducts, lastProduct, extraSellers, } = props;
  const [selectedExtraSellerId, setSelectedExtraSellerId] = useState(null);
  const [skuSearch, setSkuSearch] = useState('');
  const [isTooManyProducts, toggleTooManyProducts] = useToggle(false);
  const amazonProductsQuery = (_ => skuSearch ? _.where('SellerSKU', '==', skuSearch) : _)(tenant.ref.collection('amazonProducts').where('extraSellerId', '==', selectedExtraSellerId).limit(maxAmazonProductsCount));
  const { items: amazonProducts, isLoading: isLoadingAmazonProducts, } = useCollectionSubscription(amazonProductsQuery, [tenant.id, selectedExtraSellerId, skuSearch], { detail: true });
  const sortedAmazonProducts = sortBy(amazonProducts.filter(_ => _.SellerSKU), 'SellerSKU');
  const extraSellerOptions = [{ ...tenant, id: null, name: 'メイン' }, ...extraSellers].map(_ => ({ value: _.id, label: _.name, data: _, }));
  const selectedSeller = extraSellerOptions.find(_ => _.value === selectedExtraSellerId).data;
  const recentSyncAmazonProductsJobs = useCollectionSubscription(tenant.ref.collection('syncAmazonProductsJobs').where('extraSellerId', '==', selectedExtraSellerId).where('createdAt', '>=', addHours(new Date(), -1)), [selectedExtraSellerId]);
  const [keyword, setKeyword] = useState('');
  const [deliveryRouteValues, setDeliveryRouteValues] = useState({});
  const productSkus = chunkProducts.filter(_ => _.extraSellerId === undefined || _.extraSellerId === selectedExtraSellerId).map(_ => _.sku);
  const leftAmazonProducts = sortedAmazonProducts.filter(_ => !productSkus.includes(_.SellerSKU));
  const filteredAmazonProducts = leftAmazonProducts.filter((amazonProduct) => {
    const {
      AttributeSets: { ItemAttributes: { Title: title, }, },
      Identifiers: { MarketplaceASIN: { ASIN: asin, } },
      SellerSKU: sku,
    } = amazonProduct;
    return condition(amazonProduct) === 'New' && deliveryRouteValue(amazonProduct) && (isEmpty(keyword) || [title, asin, sku].some(_ => _?.match(keyword)));
  });
  const isLoading = recentSyncAmazonProductsJobs.some(_ => _.status === 'initial');
  const errorJob = recentSyncAmazonProductsJobs.find(_ => _.status === 'failed');
  const [selectedItems, { set: setSelectedItems, push: selectItem, removeAt: removeSelectedItemAt }] = useList([]);
  const unselectItem = _ => removeSelectedItemAt(selectedItems.indexOf(_));
  const isSelecting = selectedItems.length > 0;
  const onClickLoad = async () => {
    try {
      await batch(db, recentSyncAmazonProductsJobs.filter(_ => _.status === 'failed'), (batch, job) => batch.delete(job.ref));
      await tenant.ref.collection('syncAmazonProductsJobs').add({ version: 3, status: 'initial', extraSellerId: selectedExtraSellerId, offset: 0, createdAt: new Date() });
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };
  const onClickAdd = async (amazonProducts) => {
    const existingProducts = await getAllCollectionDataByChunk(tenant.ref.collection('products').where('isAmazonProduct', '==', true).where('extraSellerId', '==', selectedExtraSellerId));
    const existingProductSkus = existingProducts.map(_ => _.sku);
    try {
      const filteredAmazonProducts = amazonProducts.filter(_ => !existingProductSkus.includes(_.SellerSKU));
      const rowsForCreate = filteredAmazonProducts.map((amazonProduct, i) => {
        const {
          AttributeSets: { ItemAttributes: { Title: title, }, },
          Identifiers: { MarketplaceASIN: { ASIN: asin, } },
          SellerSKU: sku,
        } = amazonProduct;
        return {
          productId: (parseInt(newId(lastProduct), 10) + i).toString(),
          sku,
          data: {
            asin,
            name: title,
            sku,
            itemName: title.slice(0, 10),
            price: price(amazonProduct) || 0,
            deliveryRoute: ({ AMAZON: 'amazon', MERCHANT: 'seller', AMAZON_JP: 'amazon', DEFAULT: 'seller', })[deliveryRouteValue(amazonProduct)] || deliveryRouteValues[sku],
            condition: 'new',
            amazonProduct,
            extraSellerId: amazonProduct.extraSellerId || null,
            isAmazonProduct: true,
            fnsku: null,
            postedInventoryAvailabilityCount: 99, // NOTE: 在庫情報をpostする必要がないので
            requestsNeko: false,
            goodInventory: 0,
            ngInventory: 0,
            shippingSize: null,
            createdAt: new Date(),
          },
        };
      });
      await createProductsWithSkuCheck(db, tenant.ref.collection('products'), rowsForCreate);
      toast.success('追加しました');
      setSelectedItems([]);
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };
  const deliveryRouteOptions = entries(deliveryRoutes).map(([k, v]) => ({ label: v.label, value: k }));
  useEffect(() => {
    if(amazonProducts.length >= maxAmazonProductsCount) toggleTooManyProducts(true);
  }, [amazonProducts.length]);

  const onClickDownloadSkuSampleCsv = () => {
    const data = [
      { SKU: 'sku_example_001' },
      { SKU: 'sku_example_002' },
      { SKU: 'sku_example_003' },
    ];
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const content = encoder.encode(unparseCsv(data));
    fileDownload(content, 'SKU一括取り込み_サンプル.csv');
  };

  const processSkuCsvRows = async (rows) => {
    const skus = rows
      .filter(_ => _.SKU)
      .map(_ => _.SKU.trim())
      .filter(_ => _);

    if(skus.length === 0) {
      toast.error('有効なSKUが見つかりませんでした');
      return;
    }

    try {
      const { data: fetchedProducts } = await fetchAmazonProducts({
        tenantId: tenant.id,
        source: 'listings',
        extraSellerId: selectedExtraSellerId,
        ids: skus,
      });
      const matchedProducts = (fetchedProducts || []).map(_ => ({ ..._, extraSellerId: selectedExtraSellerId }));
      const notFoundSkus = skus.filter(sku => !matchedProducts.some(_ => _.SellerSKU === sku));

      if(notFoundSkus.length > 0) {
        toast.warning(`${notFoundSkus.length}件のSKUが見つかりませんでした`);
      }

      if(matchedProducts.length > 0) {
        await onClickAdd(matchedProducts);
        toast.success(`${matchedProducts.length}件の商品を取り込みました`);
      } else {
        toast.error('取り込める商品が見つかりませんでした');
      }
    } catch(e) {
      console.error(e);
      toast.error('取り込みに失敗しました');
    }
  };

  return (
    <div>
      {
        tenant.tenantOptionValues?.includes('multiSellers') && (
          <div>
            <div className="text-muted small">ショップ</div>
            <div style={{ width: 200 }}>
              <Select
                options={extraSellerOptions}
                value={extraSellerOptions.find(_ => _.value === selectedExtraSellerId) || null}
                onChange={_ => setSelectedExtraSellerId(_?.value)}
                className="w-100"
              />
            </div>
          </div>
        )
      }
      {
        !isSelecting ? (
          <div>
            <div className="mb-3 d-flex justify-content-end align-items-center gap-2">
              <Button color="primary" onClick={onClickLoad} disabled={isLoading}>
                <span className={classnames('fas mr-1', { 'fa-sync': !isLoading, 'fa-spin fa-spinner': isLoading })} />
                読み込み
              </Button>
            </div>
          </div>
        ) : (
          <div className="mb-3 d-flex justify-content-end align-items-center">
            <div className="d-flex align-items-end">
              <div>
                {selectedItems.length} 件を選択中
              </div>
              <ProgressButton className="ml-2" process={onClickAdd.bind(null, selectedItems)}>
                <span className="fas fa-plus mr-1" />
                取り込む
              </ProgressButton>
            </div>
          </div>
        )
      }
      {
        isTooManyProducts && (
          <div className="alert alert-warning">
            Amazonから読み込んだ商品が{maxAmazonProductsCount.toLocaleString()}件以上あります。この一覧に表示されないものは<strong>SKU完全一致検索</strong> または <strong>CSVで一括取り込み</strong>をご利用ください。
          </div>
        )
      }
      {
        isTooManyProducts && (
          <div className="mb-3 d-flex justify-content-start align-items-center gap-2">
            <div style={{ width: 350 }}>
              <Input type="search" value={skuSearch} onChange={_ => setSkuSearch(_.target.value)} placeholder="SKU完全一致検索" />
            </div>
            <div className="text-muted">または</div>
            <Button onClick={onClickDownloadSkuSampleCsv} size="sm">
              <span className="fas fa-download mr-1" />
              サンプルCSV
            </Button>
            <ImportButton
              processRows={processSkuCsvRows}
              size="sm"
              label="CSVで一括取り込み"
            />
          </div>
        )
      }
      <div className="mb-3 d-flex justify-content-start align-items-center">
        <div style={{ width: 350 }}>
          <Input type="search" value={keyword} onChange={_ => setKeyword(_.target.value)} placeholder="ASIN、SKU、商品名で検索" disabled={selectedItems.length > 0} />
        </div>
      </div>
      <div className="alert alert-danger">
        ※ 新品のみ選択してください。出品停止中の商品は取り込みできません。
      </div>
      {
        isLoading && (
          <div className="alert alert-info">
            商品の読み込みには数分かかる場合もあります。
            <br />
            今しばらくお待ち下さいませ。
          </div>
        )
      }
      {
        errorJob != null && (
          <div className="alert alert-danger">
            商品の読み込みに失敗しました。
            {errorJob.errorMessage}
          </div>
        )
      }
      <div className="position-relative">
        <table className="table table-bordered">
          <thead className="thead-light text-nowrap">
            <tr>
              <th>
                {
                  filteredAmazonProducts.length > 0 && (
                    <Input type="checkbox" className="position-relative m-0" checked={difference(filteredAmazonProducts, selectedItems).length === 0} onChange={_ => _.target.checked ? setSelectedItems(filteredAmazonProducts) : setSelectedItems([])} />
                  )
                }
              </th>
              <th style={{ minWidth: 150 }}>配送方法</th>
              <th>画像</th>
              <th>ASIN</th>
              <th>SKU</th>
              <th>商品名</th>
              <th>Amazon価格</th>
            </tr>
          </thead>
          <tbody>
            {
              filteredAmazonProducts.map((amazonProduct) => {
                const {
                  AttributeSets: {
                    ItemAttributes: {
                      Title: title,
                      SmallImage: {
                        URL: imageUrl,
                      },
                    },
                  },
                  Identifiers: {
                    MarketplaceASIN: {
                      ASIN: asin,
                    }
                  },
                  SellerSKU: sku,
                } = amazonProduct;
                const deliveryRoute = ({ AMAZON: 'amazon', MERCHANT: 'seller', AMAZON_JP: 'amazon', DEFAULT: 'seller', })[deliveryRouteValue(amazonProduct)];
        
                return (
                  <tr key={sku}>
                    <td>
                      <Input type="checkbox" className="position-relative m-0" checked={selectedItems.includes(amazonProduct)} onChange={_ => selectedItems.includes(amazonProduct) ? unselectItem(amazonProduct) : selectItem(amazonProduct)} disabled={!(deliveryRoute || deliveryRouteValues[sku])}/>
                    </td>
                    <td>
                      {
                        deliveryRoute ? (
                          deliveryRoutes[deliveryRoute]?.label
                        ) : (
                          <Select
                            value={deliveryRouteOptions.find(_ => _.value === deliveryRouteValues[sku]) || null}
                            onChange={_ => _ && setDeliveryRouteValues({ ...deliveryRouteValues, [sku]: _.value })}
                            options={deliveryRouteOptions}
                            components={{ IndicatorsContainer: _ => null }}
                          />
                        )
                      }
                      {
                        condition(amazonProduct) == null && (
                          <div className="badge badge-danger">新品か不明</div>
                        )
                      }
                    </td>
                    <td>
                      <img src={imageUrl} style={{ maxWidth: 100, maxHeight: 100, }} />
                    </td>
                    <td>{asin}</td>
                    <td>{sku}</td>
                    <td>{title}</td>
                    <td className="text-nowrap text-right">{numeral(price(amazonProduct)).format('0,0')}</td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>
        <OverlayLoading isOpen={isLoadingAmazonProducts} label="読み込み中..." />
      </div>
    </div>
  );
}
